import { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import { apiClient } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const FeatureGateContext = createContext({
  tier: 0, unlocked: ["dashboard", "food_log"], newlyUnlocked: [], nextUnlock: null, loading: true,
  isUnlocked: () => true, refresh: () => {},
});

export const useFeatureGates = () => useContext(FeatureGateContext);

// Nav group key → required feature gate
const GROUP_GATE_MAP = {
  dashboard: null,
  plan: null,
  create: null,
  track: null,
  more: null,
};

export function FeatureGateProvider({ children }) {
  const { user } = useAuth();
  const [data, setData] = useState({ tier: 0, unlocked: ["dashboard", "food_log"], newlyUnlocked: [], nextUnlock: null });
  const [loading, setLoading] = useState(true);

  const fetchGates = useCallback(async () => {
    if (!user) { setLoading(false); return; }
    try {
      const res = await apiClient.get("/user/feature-gates");
      setData(res.data);
    } catch {
      // Keep restricted defaults — don't fail-open
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { fetchGates(); }, [fetchGates]);

  const isUnlocked = useCallback((featureKey) => {
    if (!featureKey) return true;
    return data.unlocked.includes(featureKey);
  }, [data.unlocked]);

  const isGroupUnlocked = useCallback((groupKey) => {
    const gate = GROUP_GATE_MAP[groupKey];
    if (gate === undefined || gate === null) return true;
    return data.unlocked.includes(gate);
  }, [data.unlocked]);

  const isNewlyUnlocked = useCallback((featureKey) => {
    return data.newlyUnlocked?.includes(featureKey) || false;
  }, [data.newlyUnlocked]);

  const ctxValue = useMemo(() => ({
    tier: data.tier,
    unlocked: data.unlocked,
    newlyUnlocked: data.newlyUnlocked || [],
    nextUnlock: data.nextUnlock,
    loading,
    isUnlocked,
    isGroupUnlocked,
    isNewlyUnlocked,
    refresh: fetchGates,
  }), [data, loading, isUnlocked, isGroupUnlocked, isNewlyUnlocked, fetchGates]);

  return (
    <FeatureGateContext.Provider value={ctxValue}>
      {children}
    </FeatureGateContext.Provider>
  );
}
